<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { OwnerUserData } from "@core/composables"
import type { UserSuggestion } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            user: {
                fieldsetTitle: "Coordonnées",
                firstname: {
                    label: "Prénom",
                },
                lastname: {
                    label: "Nom",
                },
                email: {
                    label: "Email",
                    hint: "L'email sera utilisé pour identifier le titulaire",
                },
                search: "Rechercher",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Données utilisateur (v-model)
     */
    modelValue: OwnerUserData
    /**
     * Suggestions pour l'autocomplétion
     */
    suggestions?: UserSuggestion[]
    /**
     * Suggestions filtrées
     */
    filteredSuggestions?: UserSuggestion[]
    /**
     * Indique si la recherche de suggestions est en cours
     */
    isSearchingSuggestions?: boolean
    /**
     * Indique si la recherche par email est en cours
     */
    isSearchingByEmail?: boolean
    /**
     * Indique si la recherche a été effectuée
     */
    hasSearched?: boolean
    /**
     * Erreur sur le prénom
     */
    errorFirstname?: string
    /**
     * Erreur sur le nom
     */
    errorLastname?: string
    /**
     * Erreur sur l'email
     */
    errorEmail?: string
    /**
     * Affiche le bouton rechercher
     */
    showSearchButton?: boolean
    /**
     * Désactive le bouton rechercher
     */
    disableSearchButton?: boolean
}

interface Emits {
    /**
     * Émis lors de la mise à jour des données
     */
    (e: "update:modelValue", value: OwnerUserData): void
    /**
     * Émis lors de la recherche de suggestions
     */
    (e: "complete", query: string): void
    /**
     * Émis lors de la sélection d'une suggestion
     */
    (e: "select", suggestion: UserSuggestion): void
    /**
     * Émis lors du changement de l'email
     */
    (e: "email-change"): void
    /**
     * Émis lors du clic sur rechercher
     */
    (e: "search"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    suggestions: () => [],
    filteredSuggestions: () => [],
    isSearchingSuggestions: false,
    isSearchingByEmail: false,
    hasSearched: false,
    errorFirstname: "",
    errorLastname: "",
    errorEmail: "",
    showSearchButton: false,
    disableSearchButton: false,
})

const emit = defineEmits<Emits>()

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Prénom (v-model local)
 */
const getFirstname = computed({
    get: () => props.modelValue.firstname,
    set: (value: string) => {
        emit("update:modelValue", {
            ...props.modelValue,
            firstname: value,
        })
    },
})

/**
 * Nom (v-model local)
 */
const getLastname = computed({
    get: () => props.modelValue.lastname,
    set: (value: string) => {
        emit("update:modelValue", {
            ...props.modelValue,
            lastname: value,
        })
    },
})

/**
 * Email (v-model local)
 */
const getUsername = computed({
    get: () => props.modelValue.username,
    set: (value: string) => {
        emit("update:modelValue", {
            ...props.modelValue,
            username: value,
        })
    },
})

// ================================
// INTERACTIONS
// ================================

/**
 * Gère la recherche de suggestions
 * @param event - Événement de complétion
 */
function onComplete(event: { query: string }): void {
    emit("complete", event.query)
}

/**
 * Gère la sélection d'une suggestion
 * @param event - Événement de sélection
 */
function onSelectSuggestion(event: { value: UserSuggestion }): void {
    emit("select", event.value)
}

/**
 * Gère le changement de l'email
 */
function onEmailChange(): void {
    emit("email-change")
}

/**
 * Gère le clic sur rechercher
 */
function onClickSearch(): void {
    emit("search")
}
</script>

<template>
    <div class="owner-user-form flex flex-col gap-4">
        <!-- ================================ -->
        <!-- FORMULAIRE COORDONNÉES -->
        <!-- ================================ -->
        <fieldset class="flex flex-col gap-4">
            <legend class="text-base font-medium text-gray-700 mb-2">
                {{ t("user.fieldsetTitle") }}
            </legend>

            <!-- ================================ -->
            <!-- NOM -->
            <!-- ================================ -->
            <BaseInputWrapper
                input-id="owner-lastname"
                :label="t('user.lastname.label')"
                :error="errorLastname"
                required
            >
                <BaseInputText
                    id="owner-lastname"
                    v-model="getLastname"
                    fluid
                    :invalid="!!errorLastname"
                />
            </BaseInputWrapper>

            <!-- ================================ -->
            <!-- PRÉNOM -->
            <!-- ================================ -->
            <BaseInputWrapper
                input-id="owner-firstname"
                :label="t('user.firstname.label')"
                :error="errorFirstname"
                required
            >
                <BaseInputText
                    id="owner-firstname"
                    v-model="getFirstname"
                    fluid
                    :invalid="!!errorFirstname"
                />
            </BaseInputWrapper>

            <!-- ================================ -->
            <!-- EMAIL AVEC AUTOCOMPLÉTION -->
            <!-- ================================ -->
            <BaseInputWrapper
                input-id="owner-email"
                :label="t('user.email.label')"
                :hint="t('user.email.hint')"
                :error="errorEmail"
                required
            >
                <BaseAutoComplete
                    id="owner-email"
                    v-model="getUsername"
                    :suggestions="filteredSuggestions"
                    option-label="name"
                    :loading="isSearchingSuggestions"
                    fluid
                    :invalid="!!errorEmail"
                    @complete="onComplete"
                    @item-select="onSelectSuggestion"
                    @input="onEmailChange"
                >
                    <template #option="{ option }">
                        <ItemUser :user="option.user" />
                    </template>
                </BaseAutoComplete>
            </BaseInputWrapper>
        </fieldset>

        <!-- ================================ -->
        <!-- BOUTON RECHERCHER -->
        <!-- ================================ -->
        <div
            v-if="showSearchButton && !hasSearched"
            class="flex justify-center mt-2"
        >
            <BaseButton
                type="button"
                :label="t('user.search')"
                :disabled="disableSearchButton"
                :loading="isSearchingByEmail"
                @click="onClickSearch"
            />
        </div>
    </div>
</template>
